import React, { useState, useMemo } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Filter, Clock, DollarSign, MapPin, BookOpen, TrendingUp } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { SEO } from './SEO';
import { buildBreadcrumb } from '../lib/seo-schemas';

// All programs consolidated from all 9 partner institutions
const allPrograms = [
  // Cambria College (5 programs)
  { id: 1, name: 'Health Care Assistant', institution: 'Cambria College', institutionSlug: 'cambria-college', category: 'Healthcare & Medical', duration: '28 weeks', tuition: 'Domestic: $6,800 | International: $13,000', location: 'Victoria, Surrey, Vancouver, BC', careers: ['Health Care Assistant', 'Care Aide'] },
  { id: 2, name: 'Medical Office Assistant', institution: 'Cambria College', institutionSlug: 'cambria-college', category: 'Healthcare & Medical', duration: '30 weeks', tuition: 'Domestic: $6,500 | International: $12,500', location: 'Victoria, Surrey, Vancouver, BC', careers: ['Medical Office Assistant', 'Healthcare Administrator'] },
  { id: 3, name: 'Digital Business Management', institution: 'Cambria College', institutionSlug: 'cambria-college', category: 'Business & Hospitality', duration: '80 weeks (with co-op)', tuition: 'Domestic: $9,000 | International: $17,000', location: 'Victoria, Surrey, Vancouver, BC', careers: ['Digital Marketing Specialist', 'Social Media Manager', 'Business Coordinator'] },
  { id: 4, name: 'Hospitality Management', institution: 'Cambria College', institutionSlug: 'cambria-college', category: 'Business & Hospitality', duration: '96 weeks (with co-op)', tuition: 'Domestic: $7,800 | International: $14,500', location: 'Victoria, Surrey, Vancouver, BC', careers: ['Hotel Manager', 'Restaurant Manager', 'Event Coordinator'] },
  { id: 5, name: 'Mental Health Support Worker', institution: 'Cambria College', institutionSlug: 'cambria-college', category: 'Counselling & Mental Health', duration: '35 weeks', tuition: 'Domestic: $7,000 | International: $13,000', location: 'Victoria, Surrey, Vancouver, BC', careers: ['Mental Health Worker', 'Community Support Worker'] },
  
  // Granville College (5 programs)
  { id: 6, name: 'Veterinary Technician Assistant', institution: 'Granville College', institutionSlug: 'granville-college', category: 'Healthcare & Medical', duration: '36 weeks', tuition: 'Domestic: $6,500 | International: $12,500', location: 'Vancouver & Surrey, BC', careers: ['Veterinary Assistant', 'Animal Care Technician'] },
  { id: 7, name: 'Healthcare Office Administration', institution: 'Granville College', institutionSlug: 'granville-college', category: 'Healthcare & Medical', duration: '43 weeks', tuition: 'Domestic: $7,000 | International: $13,000', location: 'Vancouver & Surrey, BC', careers: ['Medical Office Administrator', 'Healthcare Coordinator'] },
  { id: 8, name: 'Accounting & Payroll Administration', institution: 'Granville College', institutionSlug: 'granville-college', category: 'Business & Hospitality', duration: '43 weeks', tuition: 'Domestic: $6,800 | International: $12,500', location: 'Vancouver & Surrey, BC', careers: ['Accounting Technician', 'Payroll Administrator'] },
  { id: 9, name: 'Hospitality Management', institution: 'Granville College', institutionSlug: 'granville-college', category: 'Business & Hospitality', duration: '40 weeks', tuition: 'Domestic: $6,200 | International: $11,500', location: 'Vancouver & Surrey, BC', careers: ['Hotel Manager', 'Front Office Supervisor'] },
  { id: 10, name: 'Paralegal Studies', institution: 'Granville College', institutionSlug: 'granville-college', category: 'Community Services', duration: '45 weeks', tuition: 'Domestic: $7,200 | International: $13,500', location: 'Vancouver & Surrey, BC', careers: ['Paralegal', 'Legal Assistant'] },
  
  // Swenam College (4 programs)
  { id: 11, name: 'Business Administration', institution: 'Swenam College', institutionSlug: 'swenam-college', category: 'Business & Hospitality', duration: '2 years', tuition: 'Domestic: $7,500/year | International: $14,000/year', location: 'British Columbia', careers: ['Business Manager', 'Operations Manager'] },
  { id: 12, name: 'Psychology', institution: 'Swenam College', institutionSlug: 'swenam-college', category: 'Community Services', duration: '2 years', tuition: 'Domestic: $7,000/year | International: $13,500/year', location: 'British Columbia', careers: ['Psychology Researcher', 'Mental Health Worker'] },
  { id: 13, name: 'Nutrition & Food Service Management', institution: 'Swenam College', institutionSlug: 'swenam-college', category: 'Healthcare & Medical', duration: '2 years', tuition: 'Domestic: $7,200/year | International: $13,800/year', location: 'British Columbia', careers: ['Nutritionist', 'Food Service Manager'] },
  { id: 14, name: 'Computer Science', institution: 'Swenam College', institutionSlug: 'swenam-college', category: 'Technology & IT', duration: '2 years', tuition: 'Domestic: $8,000/year | International: $15,000/year', location: 'British Columbia', careers: ['Software Developer', 'IT Specialist'] },
  
  // VCCT (5 programs)
  { id: 20, name: 'Diploma of Professional Counselling', institution: 'VCCT', institutionSlug: 'vcct', category: 'Counselling & Mental Health', duration: '50 weeks', tuition: 'Domestic: $8,800 | International: $16,500', location: 'Vancouver, BC', careers: ['Professional Counsellor', 'Mental Health Counsellor'] },
  { id: 21, name: 'Addictions Worker Certificate', institution: 'VCCT', institutionSlug: 'vcct', category: 'Counselling & Mental Health', duration: '26 weeks', tuition: 'Domestic: $3,800 | International: $7,000', location: 'Vancouver, BC', careers: ['Addictions Counsellor', 'Recovery Support Worker'] },
  { id: 22, name: 'Youth & Family Support Worker', institution: 'VCCT', institutionSlug: 'vcct', category: 'Community Services', duration: '24 weeks', tuition: 'Domestic: $3,800 | International: $7,000', location: 'Vancouver, BC', careers: ['Youth Counsellor', 'Family Support Worker'] },
  { id: 23, name: 'Social Service Worker Diploma', institution: 'VCCT', institutionSlug: 'vcct', category: 'Community Services', duration: '48 weeks', tuition: 'Domestic: $8,500 | International: $16,000', location: 'Vancouver, BC', careers: ['Social Service Worker', 'Community Counsellor'] },
  { id: 24, name: 'Indigenous Community Service Worker', institution: 'VCCT', institutionSlug: 'vcct', category: 'Community Services', duration: '48 weeks', tuition: 'Domestic: $8,500 | International: $16,000', location: 'Vancouver, BC', careers: ['Indigenous Support Worker', 'Community Liaison'] },
  
  // Avalon Community College (5 programs)
  { id: 25, name: 'Business Administration Diploma', institution: 'Avalon Community College', institutionSlug: 'avalon-community-college', category: 'Business & Hospitality', duration: '40 weeks', tuition: 'Domestic: $7,000 | International: $13,500', location: 'Surrey, BC', careers: ['Business Manager', 'Administrative Coordinator'] },
  { id: 26, name: 'Hospitality Management (with Co-op)', institution: 'Avalon Community College', institutionSlug: 'avalon-community-college', category: 'Business & Hospitality', duration: '60 weeks', tuition: 'Domestic: $8,500 | International: $16,000', location: 'Surrey, BC', careers: ['Hotel Manager', 'Hospitality Coordinator'] },
  { id: 27, name: 'Veterinary Technician Assistant', institution: 'Avalon Community College', institutionSlug: 'avalon-community-college', category: 'Healthcare & Medical', duration: '24 weeks', tuition: 'Domestic: $6,500 | International: $12,500', location: 'Surrey, BC', careers: ['Veterinary Assistant', 'Animal Care Technician'] },
  { id: 28, name: 'Healthcare Office Administration', institution: 'Avalon Community College', institutionSlug: 'avalon-community-college', category: 'Healthcare & Medical', duration: '40 weeks', tuition: 'Domestic: $7,000 | International: $13,000', location: 'Surrey, BC', careers: ['Medical Office Assistant', 'Healthcare Administrator'] },
  { id: 29, name: 'Hospitality Management (without Co-op)', institution: 'Avalon Community College', institutionSlug: 'avalon-community-college', category: 'Business & Hospitality', duration: '48 weeks', tuition: 'Domestic: $7,500 | International: $14,000', location: 'Surrey, BC', careers: ['Hotel Supervisor', 'Event Coordinator'] },
  
  // Eton College (5 programs)
  { id: 30, name: 'Flight Attendant Training', institution: 'Eton College', institutionSlug: 'eton-college', category: 'Aviation & Travel', duration: '26 weeks', tuition: 'Domestic: $8,000 | International: $15,000', location: 'Vancouver, BC', careers: ['Flight Attendant', 'Cabin Crew'] },
  { id: 31, name: 'Healthcare Office Administration', institution: 'Eton College', institutionSlug: 'eton-college', category: 'Healthcare & Medical', duration: '35 weeks', tuition: 'Domestic: $6,800 | International: $13,000', location: 'Vancouver, BC', careers: ['Medical Office Assistant', 'Healthcare Administrator'] },
  { id: 32, name: 'Hospitality Management', institution: 'Eton College', institutionSlug: 'eton-college', category: 'Business & Hospitality', duration: '40 weeks', tuition: 'Domestic: $7,200 | International: $13,800', location: 'Vancouver, BC', careers: ['Hotel Manager', 'Hospitality Coordinator'] },
  { id: 33, name: 'Business Administration', institution: 'Eton College', institutionSlug: 'eton-college', category: 'Business & Hospitality', duration: '48 weeks', tuition: 'Domestic: $7,500 | International: $14,200', location: 'Vancouver, BC', careers: ['Business Manager', 'Operations Coordinator'] },
  { id: 34, name: 'Airport Operations Management', institution: 'Eton College', institutionSlug: 'eton-college', category: 'Aviation & Travel', duration: '30 weeks', tuition: 'Domestic: $7,800 | International: $14,800', location: 'Vancouver, BC', careers: ['Airport Operations Manager', 'Ground Operations Coordinator'] },
  
  // Multihexa College (5 programs)
  { id: 35, name: 'Web Development Diploma', institution: 'Multihexa College', institutionSlug: 'multihexa-college', category: 'Technology & IT', duration: '52 weeks', tuition: 'Domestic: $9,000 | International: $17,000', location: 'Vancouver, BC', careers: ['Web Developer', 'Full Stack Developer'] },
  { id: 36, name: 'Digital Marketing Certificate', institution: 'Multihexa College', institutionSlug: 'multihexa-college', category: 'Business & Hospitality', duration: '30 weeks', tuition: 'Domestic: $7,500 | International: $14,500', location: 'Vancouver, BC', careers: ['Digital Marketing Specialist', 'Social Media Manager'] },
  { id: 37, name: 'Graphic Design Diploma', institution: 'Multihexa College', institutionSlug: 'multihexa-college', category: 'Technology & IT', duration: '48 weeks', tuition: 'Domestic: $8,500 | International: $16,000', location: 'Vancouver, BC', careers: ['Graphic Designer', 'UI/UX Designer'] },
  { id: 38, name: 'Network Administration', institution: 'Multihexa College', institutionSlug: 'multihexa-college', category: 'Technology & IT', duration: '40 weeks', tuition: 'Domestic: $8,200 | International: $15,500', location: 'Vancouver, BC', careers: ['Network Administrator', 'IT Support Specialist'] },
  { id: 39, name: 'Business Management Diploma', institution: 'Multihexa College', institutionSlug: 'multihexa-college', category: 'Business & Hospitality', duration: '45 weeks', tuition: 'Domestic: $7,800 | International: $14,800', location: 'Vancouver, BC', careers: ['Business Manager', 'Project Manager'] },
  
  // Taylor Pro College (5 programs)
  { id: 40, name: 'Class 1 Driver Training (MELT)', institution: 'Taylor Pro College', institutionSlug: 'taylor-pro-college', category: 'Transportation & Trades', duration: '8-12 weeks', tuition: 'Domestic: $8,500 | International: $16,000', location: 'Vancouver, Vernon & Kelowna, BC', careers: ['Commercial Truck Driver', 'Transport Driver'] },
  { id: 41, name: 'Business Administration Diploma', institution: 'Taylor Pro College', institutionSlug: 'taylor-pro-college', category: 'Business & Hospitality', duration: '52 weeks', tuition: 'Domestic: $7,500 | International: $14,000', location: 'Vancouver, Vernon & Kelowna, BC', careers: ['Business Manager', 'Administrative Coordinator'] },
  { id: 42, name: 'Addictions & Community Support Worker', institution: 'Taylor Pro College', institutionSlug: 'taylor-pro-college', category: 'Community Services', duration: '52 weeks', tuition: 'Domestic: $8,000 | International: $15,500', location: 'Vancouver, Vernon & Kelowna, BC', careers: ['Addiction Counsellor', 'Community Support Worker'] },
  { id: 43, name: 'Social Services Support Worker', institution: 'Taylor Pro College', institutionSlug: 'taylor-pro-college', category: 'Community Services', duration: '52 weeks', tuition: 'Domestic: $8,000 | International: $15,500', location: 'Vancouver, Vernon & Kelowna, BC', careers: ['Social Service Worker', 'Community Counsellor'] },
  { id: 44, name: 'Office Administration Diploma', institution: 'Taylor Pro College', institutionSlug: 'taylor-pro-college', category: 'Business & Hospitality', duration: '40 weeks', tuition: 'Domestic: $6,800 | International: $13,000', location: 'Vancouver, Vernon & Kelowna, BC', careers: ['Office Administrator', 'Administrative Assistant'] }
];

const categories = [
  'All Programs',
  'Healthcare & Medical',
  'Business & Hospitality',
  'Counselling & Mental Health',
  'Technology & IT',
  'Community Services',
  'Aviation & Travel',
  'Transportation & Trades'
];

export const ProgramsPage = () => {
  const { categorySlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const urlSearch = searchParams.get('search') || '';
  
  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const [selectedCategory, setSelectedCategory] = useState(
    location.state?.category || categorySlug || 'All Programs'
  );

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [categorySlug]);

  React.useEffect(() => {
    if (location.state?.category) {
      setSelectedCategory(location.state.category);
    }
  }, [location.state]);

  const filteredPrograms = useMemo(() => {
    return allPrograms.filter(program => {
      const matchesSearch = searchQuery === '' || 
        program.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        program.institution.toLowerCase().includes(searchQuery.toLowerCase()) ||
        program.careers.some(career => career.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'All Programs' || program.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 500);
  };

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: filteredPrograms.slice(0, 20).map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: p.name,
      url: `https://elevatededucation.ca/institutions/${p.institutionSlug}`,
    })),
  };

  return (
    <div className="min-h-screen pt-32 pb-16 bg-gray-50">
      <SEO
        title="Career College Programs in BC – Diplomas & Certificates"
        description="Browse career-focused diploma and certificate programs across BC career colleges. Filter by healthcare, business, IT, counselling, trades, and more."
        path="/programs"
        schema={[
          itemListSchema,
          buildBreadcrumb([
            { name: 'Home', path: '/' },
            { name: 'Programs', path: '/programs' },
          ]),
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/#programs"
          className="inline-flex items-center text-gray-600 hover:text-blue-600 mb-8 transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Explore Our Programs
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Browse career-focused programs from our BC partner colleges
          </p>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search programs, colleges, or careers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-6 text-lg"
                />
              </div>
            </div>
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full h-full px-4 py-3 border-2 border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-blue-500"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing <span className="font-semibold text-gray-900">{filteredPrograms.length}</span> program{filteredPrograms.length !== 1 ? 's' : ''}
            {selectedCategory !== 'All Programs' && <span> in <span className="font-semibold text-blue-600">{selectedCategory}</span></span>}
          </p>
        </div>

        {/* Programs Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          {filteredPrograms.map(program => (
            <div key={program.id} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {program.name}
                  </h3>
                  <Link 
                    to={`/institutions/${program.institutionSlug}`}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm hover:underline"
                  >
                    {program.institution}
                  </Link>
                </div>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                  {program.category}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="w-4 h-4 mr-2 text-blue-600" />
                  {program.duration}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <DollarSign className="w-4 h-4 mr-2 text-green-600" />
                  {program.tuition}
                </div>
                <div className="flex items-center text-sm text-gray-600 col-span-2">
                  <MapPin className="w-4 h-4 mr-2 text-purple-600" />
                  {program.location}
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2 font-medium flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2 text-blue-600" />
                  Careers:
                </p>
                <div className="flex flex-wrap gap-2">
                  {program.careers.map((career, idx) => (
                    <span key={idx} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {career}
                    </span>
                  ))}
                </div>
              </div>

              <Button 
                onClick={scrollToContact}
                variant="outline"
                className="w-full hover:bg-blue-600 hover:text-white hover:border-blue-600"
              >
                Request Information
              </Button>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredPrograms.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No programs found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
            <Button onClick={() => { setSearchQuery(''); setSelectedCategory('All Programs'); }}>
              Clear Filters
            </Button>
          </div>
        )}

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-12 text-white text-center shadow-xl">
          <h2 className="text-3xl font-bold mb-4">Need Help Choosing?</h2>
          <p className="text-xl mb-8 opacity-90">
            Book a free consultation with our education advisors to find the perfect program for you.
          </p>
          <Button 
            onClick={scrollToContact}
            className="bg-white text-blue-600 hover:bg-gray-100"
            size="lg"
          >
            Book Free Consultation
          </Button>
        </div>
      </div>
    </div>
  );
};
